import type {
  MetricCatalogUpdateRequest,
  MetricReportUpdateRequest,
} from "contracts/api/metric/requests";
import type { MetricDetail as MetricDetailData } from "contracts/api/metric/responses";
import { PILLARS } from "contracts/enums/metrics";
import { createSignal, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { api } from "../../store/api";
import { BlockEditor } from "../admin/BlockEditor";
import { NumberField, TextArea, TextField } from "../admin/editorFields";

export function MetricEditor(props: {
  websiteId: string;
  metricKey: string;
  value: MetricDetailData;
  onClose: () => void;
  onSaved: (fresh: MetricDetailData) => void;
}) {
  const [catalog, setCatalog] = createStore<MetricCatalogUpdateRequest>({
    label: props.value.metric.label,
    pillar: props.value.metric.pillar as MetricCatalogUpdateRequest["pillar"],
    target: props.value.metric.target,
    good: props.value.metric.good,
    watch: props.value.metric.watch,
    description: props.value.metric.description,
  });
  const [report, setReport] = createStore<MetricReportUpdateRequest>({
    score: props.value.signal?.score ?? 0,
    delta: props.value.signal?.delta ?? 0,
    detail: props.value.signal?.detail ?? "No data yet",
    blocks: props.value.blocks,
  });
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.updateMetricCatalog(props.metricKey, catalog);
      const fresh = await api.updateMetricReport(
        props.websiteId,
        props.metricKey,
        report,
      );
      props.onSaved(fresh);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Metric save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="scrim scrim-right">
      <aside class="drawer drawer-wide">
        <div class="drawer-head">
          <h3 class="drawer-title">Edit metric</h3>
          <button
            class="icon-btn"
            type="button"
            aria-label="Close"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>
        <div class="drawer-body editor">
          <fieldset class="editor-group">
            <legend>Catalog</legend>
            <TextField
              label="Label"
              value={catalog.label}
              onInput={(value) => setCatalog("label", value)}
            />
            <label class="editor-field">
              <span class="editor-field-label">Pillar</span>
              <select
                class="select"
                value={catalog.pillar}
                onChange={(event) =>
                  setCatalog(
                    "pillar",
                    event.currentTarget
                      .value as MetricCatalogUpdateRequest["pillar"],
                  )
                }
              >
                {PILLARS.map((pillar) => (
                  <option value={pillar}>{pillar}</option>
                ))}
              </select>
            </label>
            <NumberField
              label="Target"
              value={catalog.target}
              onInput={(value) => setCatalog("target", value)}
            />
            <NumberField
              label="Good"
              value={catalog.good}
              onInput={(value) => setCatalog("good", value)}
            />
            <NumberField
              label="Watch"
              value={catalog.watch}
              onInput={(value) => setCatalog("watch", value)}
            />
            <TextArea
              label="Description"
              rows={5}
              value={catalog.description}
              onInput={(value) => setCatalog("description", value)}
            />
          </fieldset>
          <fieldset class="editor-group">
            <legend>Latest report</legend>
            <NumberField
              label="Score"
              value={report.score}
              onInput={(value) => setReport("score", value)}
            />
            <NumberField
              label="Delta"
              value={report.delta}
              onInput={(value) => setReport("delta", value)}
            />
            <TextField
              label="Signal detail"
              value={report.detail}
              onInput={(value) => setReport("detail", value)}
            />
            <BlockEditor
              blocks={report.blocks}
              onChange={(blocks) => setReport("blocks", blocks)}
            />
          </fieldset>
        </div>
        <div class="editor-foot">
          <Show when={error()}>
            <span class="gate-error">{error()}</span>
          </Show>
          <button
            class="btn"
            type="button"
            disabled={saving()}
            onClick={props.onClose}
          >
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="button"
            disabled={saving()}
            onClick={save}
          >
            {saving() ? "Saving…" : "Save"}
          </button>
        </div>
      </aside>
    </div>
  );
}
